import type { ChangeEvent, FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import { ReactSortable } from "react-sortablejs";

type AddonGroup = {
  option_id?: string;
  item_id?: string;
};

type AssignedItem = {
  _id?: string;
  item_name?: string;
};

type Addon = {
  addon_name?: string;
  assigned_items?: AssignedItem[];
};

type AddonForm = {
  values: {
    addons?: AddonGroup[];
  };
  errors?: {
    addons?: string | Array<{ item_id?: string }>;
  };
  handleChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  handleBlur: (event: FocusEvent<HTMLSelectElement>) => void;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
};

type AddonGroupRowsProps = {
  form: AddonForm;
  getAddon: (id?: string) => Addon;
  isOptionSelected: (item: AddonGroup, option: AssignedItem, addons: AddonGroup[]) => boolean;
  removeAddon: (item: AddonGroup, form: AddonForm) => void;
};

const AddonGroupRows = ({ form, getAddon, isOptionSelected, removeAddon }: AddonGroupRowsProps) => {
  const { t } = useTranslation();
  const addons = form.values?.addons || [];
  const addonErrors = Array.isArray(form.errors?.addons) ? form.errors.addons : [];

  return (
    <div className="mb-3">
      <ReactSortable<any>
        list={addons}
        setList={(newList) => form.setFieldValue("addons", newList, true)}
        handle=".addon-group-drag-handle"
        ghostClass="option-group-sortable-ghost"
        forceFallback={true}
        fallbackOnBody={false}
        className="addon-group-sortable-list"
      >
        {addons.map((item, index) => (
          <div className="d-flex gap-3 mb-3" key={index}>
            <span className="addon-group-drag-handle cp align-self-center"><i className="ti ti-grip-vertical"></i></span>
            <input className="form-control col" type="text" disabled value={getAddon(item?.option_id)?.addon_name} />
            <div className="position-relative col">
              <select
                className="form-control cp"
                name={`addons[${index}].item_id`}
                value={addons[index]?.item_id}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              >
                <option value={""}>{t("label_select_item")}</option>
                {getAddon(item?.option_id)?.assigned_items?.map((option, optionIndex) => (
                  <option
                    key={optionIndex}
                    value={option?._id}
                    disabled={isOptionSelected(item, option, addons)}
                  >
                    {option?.item_name}
                  </option>
                ))}
              </select>
              {addonErrors[index]?.item_id ? (
                <span className="d-block text-danger">{t(addonErrors[index].item_id)}</span>
              ) : null}
            </div>
            <button
              type="button"
              className="btn-icon"
              onClick={() => {
                removeAddon(item, form);
              }}
            >
              <i className="ti ti-x"></i>
            </button>
          </div>
        ))}
      </ReactSortable>
    </div>
  );
};

export default AddonGroupRows;
